import React, { useState, useEffect } from 'react';
import { Award, TrendingUp, TrendingDown, Minus, Brain, Calendar, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const MoodInsightsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.request('/api/enhancements/mood-insights')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="mood-insights-card">
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="animate-spin text-purple-500" />
        </div>
      </div>
    );
  }

  if (!data) return null;

  const trendIcon = {
    improving: <TrendingUp size={16} className="text-emerald-500" />,
    declining: <TrendingDown size={16} className="text-red-400" />,
    stable: <Minus size={16} className="text-blue-500" />,
  };

  const trendLabel = {
    improving: { text: 'Improving', color: 'text-emerald-600 bg-emerald-50' },
    declining: { text: 'Needs attention', color: 'text-red-600 bg-red-50' },
    stable: { text: 'Stable', color: 'text-blue-600 bg-blue-50' },
  };

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm" data-testid="mood-insights-card">
      <div className="flex items-center gap-2 mb-4">
        <Brain size={20} className="text-purple-500" />
        <h3 className="font-bold text-gray-900">Mood Insights</h3>
        {data.has_insights && (
          <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${trendLabel[data.trend]?.color || ''}`}>
            {trendIcon[data.trend]} {trendLabel[data.trend]?.text || data.trend}
          </span>
        )}
      </div>

      {!data.has_insights ? (
        <div className="text-center py-4">
          <Calendar size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-600">{data.message}</p>
          <div className="mt-3 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-400 rounded-full transition-all"
              style={{ width: `${(data.entries_count / data.required) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">{data.entries_count}/{data.required} check-ins</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-700 leading-relaxed">{data.insight}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{data.total_entries}</p>
              <p className="text-[10px] text-gray-500 uppercase">Entries</p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{Math.round(data.average_mood * 100)}%</p>
              <p className="text-[10px] text-gray-500 uppercase">Avg Mood</p>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{data.best_day?.mood || '--'}</p>
              <p className="text-[10px] text-gray-500 uppercase">Best Day</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoodInsightsCard;
